import { Component, OnInit, AfterViewInit, EventEmitter, Output, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { jwtDecode } from 'jwt-decode';
import { filter } from 'rxjs/operators';

declare const google: any;

/**
 * The JSON structure we expect from `users.json`.
 * Adjust if you prefer the property name 'externalUsers' or 'generalUsers'.
 */
interface UsersFile {
  internalUsers: string[];
  pilotUsers: string[];
}

@Component({
  selector: 'app-login-gmail',
  standalone: true,
  templateUrl: './login-gmail.component.html',
  imports: [CommonModule, HttpClientModule],
  styleUrls: ['./login-gmail.component.css']
})
export class LoginGmailComponent implements OnInit, AfterViewInit {
  @Output() userLoggedIn = new EventEmitter<string | null>();
  @Input() buttonIdSuffix: string = '';
  user: any = null; // Decoded user info from Google
  isAppRoute = false; // Track if we're in the /app route

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Subscribe to router events to check the current URL
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isAppRoute = event.url === '/app' || event.url.startsWith('/app/');
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    // Check initial route
    this.isAppRoute = this.router.url === '/app' || this.router.url.startsWith('/app/');
    this.loadUserFromLocalStorage();
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit called'); // Log when ngAfterViewInit is called
    this.loadGoogleApi().then(() => { // Wait for the Google API to load
      console.log('Google API loaded successfully'); // Log successful loading
      this.initializeGoogleSignIn(); // Initialize Google Sign-In once the API is loaded
    }).catch(err => {
      console.error('Failed to load Google API:', err); // Log an error if the API fails to load
    });
  }

  /**
   * Initialize Google Sign-In
   */
  initializeGoogleSignIn(): void {
    console.log('Initializing Google Sign-In'); // Log when initializing Google Sign-In
    if (typeof google === 'undefined') {
      console.error('Google API not loaded.');
      return;
    }
    google.accounts.id.initialize({
      client_id: '1018289389121-lfn7vmqnvmoa7f5pb77gptv996a6s59u.apps.googleusercontent.com', // Replace with your actual Client ID
      callback: (response: any) => this.handleCredentialResponse(response),
      ux_mode: 'popup',
      use_fedcm_for_prompt: false
    });

    setTimeout(() => {
      const buttonContainer = document.getElementById('google-signin-button' + this.buttonIdSuffix);
      if (!buttonContainer) {
        console.error('Google Sign-In button container not found!');
        return;
      }
      console.log('Rendering Google Sign-In button'); // Log when rendering the button
      google.accounts.id.renderButton(
        buttonContainer,
        {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          logo_alignment: 'left'
        }
      );
    }, 500);
  }

  /**
   * Handle the response from Google Identity Services
   */
  handleCredentialResponse(response: any): void {
    console.log('Google Auth Response:', response);
    try {
      // Decode the credential (JWT)
      const decoded: any = jwtDecode(response.credential);
      console.log('Decoded User:', decoded);

      this.user = decoded;

      // Show full name if length < 16, otherwise show only first name
      let displayName = decoded.name;
      if (displayName.length >= 16) {
        displayName = decoded.name.split(' ')[0];
      }

      // Store the user object in local storage for later reference
      localStorage.setItem('googleUser', JSON.stringify(decoded));

      // Determine user role from single JSON, then navigate
      this.determineUserRole(decoded.email)
        .then(role => {
          // Save the determined role
          localStorage.setItem('userRole', role);
          console.log('User role set to:', role);

          // IMPORTANT: First notify auth service about login
          // This will broadcast the login state to all components
          this.authService.notifyLogin();
          console.log('Auth service notified about login');

          // Then emit the event to parent component
          // This handles the specific case where the login component is embedded
          this.userLoggedIn.emit(displayName);
          console.log('Parent component notified about login with name:', displayName);

          // Force change detection
          this.cdr.detectChanges();

          // If we're not already in the app route, navigate there
          if (!this.isAppRoute) {
            this.router.navigate(['/app']);
          }
        })
        .catch(err => {
          console.error('Error determining user role:', err);
          // Fallback if JSON loading fails
          localStorage.setItem('userRole', 'general');

          // Still notify auth service even on error
          this.authService.notifyLogin();
          console.log('Auth service notified about login (fallback)');

          // Emit the event to parent component
          this.userLoggedIn.emit(displayName);
          console.log('Parent component notified about login with name:', displayName);

          // Force change detection
          this.cdr.detectChanges();

          // If we're not already in the app route, navigate there
          if (!this.isAppRoute) {
            this.router.navigate(['/app']);
          }
        });

    } catch (error) {
      console.error('Error decoding JWT:', error);
    }
  }

  /**
   * Determine the user role (internal, pilot, or general) from a single JSON file
   * using a Promise-based approach.
   */
  determineUserRole(email: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Normalize the email for matching
      const normalizedEmail = email.toLowerCase().trim();

      // Fetch the single users.json
      this.http.get<UsersFile>('assets/users.json').subscribe({
        next: (data: UsersFile) => {
          console.log('Loaded users.json:', data);

          // Check if user is in internalUsers
          const inInternal = data.internalUsers
            .map(u => u.toLowerCase().trim())
            .includes(normalizedEmail);
          if (inInternal) {
            return resolve('internal');
          }

          // Else check if user is in pilotUsers
          const inPilot = data.pilotUsers
            .map(u => u.toLowerCase().trim())
            .includes(normalizedEmail);
          if (inPilot) {
            return resolve('pilot');
          }

          // Otherwise default to 'general' (or 'external' if you prefer)
          resolve('general');
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Load user from local storage if already logged in
   */
  loadUserFromLocalStorage(): void {
    const storedUser = localStorage.getItem('googleUser');
    if (storedUser) {
      this.user = JSON.parse(storedUser);

      // Show full name if length < 16, otherwise show only first name
      let displayName = this.user.name;
      if (displayName.length >= 16) {
        displayName = this.user.name.split(' ')[0];
      }

      this.userLoggedIn.emit(displayName);
      this.cdr.detectChanges();
    }
  }

  /**
   * Logout logic: clear local storage, user object, re-render Google button
   */
  logout(): void {
    this.authService.logout();
    this.user = null;
    this.userLoggedIn.emit(null);
    this.cdr.detectChanges();

    setTimeout(() => {
      this.initializeGoogleSignIn();
    }, 100);
  }

  loadGoogleApi(): Promise<void> {
    console.log('Attempting to load Google API'); // Log when attempting to load the API
    return new Promise((resolve, reject) => { // Create a new promise to load the Google API
      if (typeof google !== 'undefined') { // Check if the Google API is already loaded
        console.log('Google API already loaded'); // Log if already loaded
        resolve(); // Resolve the promise if the API is already loaded
      } else {
        const script = document.createElement('script'); // Create a new script element
        script.src = 'https://accounts.google.com/gsi/client'; // Set the source to the Google API
        script.async = true; // Load the script asynchronously
        script.defer = true; // Defer the script execution
        script.onload = () => {
          console.log('Google API script loaded'); // Log when the script is loaded
          resolve();
        };
        script.onerror = () => {
          console.error('Google API script failed to load'); // Log if the script fails to load
          reject('Google API failed to load.');
        };
        document.head.appendChild(script); // Append the script to the document head
      }
    });
  }
}
